import { useEffect, useCallback } from 'react';

export interface KeyboardShortcutConfig {
  onSave?: () => void;
  onSend?: () => void;
  onFormatJson?: () => void;
  onCloseTab?: () => void;
  enabled?: boolean;
}

/**
 * Hook for handling keyboard shortcuts in the request builder
 *
 * Shortcuts:
 * - Cmd/Ctrl+S: Save request to collection
 * - Cmd/Ctrl+Enter: Send request
 * - Cmd/Ctrl+Shift+F: Format JSON body
 * - Cmd/Ctrl+W: Close current tab
 */
export function useKeyboardShortcuts({
  onSave,
  onSend,
  onFormatJson,
  onCloseTab,
  enabled = true,
}: KeyboardShortcutConfig) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      const isMod = e.metaKey || e.ctrlKey;

      // Cmd/Ctrl+S: Save
      if (isMod && e.key === 's' && !e.shiftKey) {
        e.preventDefault();
        onSave?.();
        return;
      }

      // Cmd/Ctrl+Enter: Send
      if (isMod && e.key === 'Enter') {
        e.preventDefault();
        onSend?.();
        return;
      }

      // Cmd/Ctrl+Shift+F: Format JSON
      if (isMod && e.shiftKey && e.key === 'f') {
        e.preventDefault();
        onFormatJson?.();
        return;
      }

      // Cmd/Ctrl+W: Close current tab
      if (isMod && e.key === 'w' && !e.shiftKey) {
        e.preventDefault();
        onCloseTab?.();
        return;
      }
    },
    [enabled, onSave, onSend, onFormatJson, onCloseTab]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

export default useKeyboardShortcuts;
